import { useCallback } from 'react'
import {
  FetchCustomerProfile,
  FetchDashboardsUpdates,
  FetchFailedPaymentsUpdates,
  FetchMarkets,
  FetchMCCustomers,
  FetchMCsWithPendingRequests,
  FetchOnboardingTemplates,
  FetchRemoteConfig,
  FetchTags,
  FetchUser,
  SetUserDataLoaded,
} from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from './useAsyncDispatchWithErrorToast'
import { AccessInfo, User } from 'src/interfaces/user'
import { DashboardsUpdatesType } from 'src/constants/activities'
import { UserPermission, UserProductName } from 'src/constants/user'
import { usePolling } from 'src/utils/hooks/usePolling'
import { useDispatch, useSelector } from 'react-redux'
import { getAccessInfo } from 'src/selectors/user'
import { intercomService } from 'src/services/IntercomService'
import useCheckPermissions from './useCheckPermissions'
import { logger } from 'src/utils/logger'
import * as Sentry from '@sentry/react'

const DASHBOARDS_UPDATES_POLLING_INTERVAL = 1000 * 60 * 3

export const useFetchUserAndData = () => {
  const dispatch = useDispatch()

  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const { hasPermission } = useCheckPermissions()

  const accessInfo = useSelector(getAccessInfo)

  const fetchUserAndMarkets = useCallback(async () => {
    const user = await asyncDispatchWithErrorToast<{ user: User; accessInfo: AccessInfo } | null>(
      FetchUser.request(),
    )

    if (!user) {
      return
    }

    intercomService.syncIdentifiedUser({
      user_id: user.user.id,
      name: `${user.user.firstName} ${user.user.lastName}`,
      email: user.user.email,
      company: {
        company_id: user?.user.partner.id ? `[Customer] ${user?.user.partner?.id}` : undefined,
        name: user?.user.partner?.name
          ? `[Customer] ${user?.user.partner?.displayName} (${user?.user.partner?.name})`
          : undefined,
      },
    })

    logger.identify({
      id: user.user.id,
      name: `${user.user.firstName} ${user.user.lastName}`,
      email: user.user.email,
      partner_id: user.user.partner.id,
      partner_name: user.user.partner.displayName,
      is_master_contractor: Boolean(user.user.partner.isMasterContractor),
      role: user.accessInfo.role,
      all_markets: user.accessInfo.allMarkets,
      has_payments: user.accessInfo.products?.some((p) => p.product === UserProductName.Payments),
      has_subcontractor: user.accessInfo.products?.some(
        (p) => p.product === UserProductName.Subcontractor,
      ),
    })

    Sentry.setUser({ id: user.user.id, email: user.user.email })
    logger.onSessionURLReady((url: string) => {
      Sentry.setTag('logrocket_session', url)
    })

    // Outside the startup batch so a slow load never holds back SetUserDataLoaded, and without
    // the toast: a failed load keeps the built-in values and shows nothing.
    dispatch(FetchRemoteConfig.request())

    const fetchPromises: Promise<any>[] = []

    if (user?.user?.partner?.isMasterContractor) {
      fetchPromises.push(asyncDispatchWithErrorToast(FetchCustomerProfile.request()))
    }

    if (user?.user?.partner?.isMasterContractor) {
      fetchPromises.push(asyncDispatchWithErrorToast(FetchMCCustomers.request()))
    }

    if (
      user.accessInfo?.permissions?.includes(UserPermission.CustomerMasterContractorView) &&
      user.accessInfo?.products?.some((item) => item.product === UserProductName.Subcontractor)
    ) {
      fetchPromises.push(asyncDispatchWithErrorToast(FetchMCsWithPendingRequests.request()))
    }

    if (user.accessInfo.allMarkets || !!user.accessInfo.markets.length) {
      fetchPromises.push(
        asyncDispatchWithErrorToast(FetchMarkets.request({ partnerId: user.user.partner.id })),
      )
    }

    if (user.accessInfo?.permissions?.includes(UserPermission.TagsView)) {
      fetchPromises.push(asyncDispatchWithErrorToast(FetchTags.request()))
    }

    if (user.accessInfo?.permissions?.includes(UserPermission.TemplatesView)) {
      fetchPromises.push(
        asyncDispatchWithErrorToast(
          FetchOnboardingTemplates.request({ partnerId: user.user.partner.id }),
        ),
      )
    }

    if (
      user.accessInfo?.permissions?.includes(UserPermission.PaymentsView) &&
      user.accessInfo?.products?.some((item) => item.product === UserProductName.Payments)
    ) {
      fetchPromises.push(asyncDispatchWithErrorToast(FetchFailedPaymentsUpdates.request()))
    }

    if (user.accessInfo?.permissions?.includes(UserPermission.PartnerDriversView)) {
      fetchPromises.push(
        asyncDispatchWithErrorToast(
          FetchDashboardsUpdates.request({ type: DashboardsUpdatesType.DisabledContractors }),
        ),
      )
    }

    if (user.accessInfo?.permissions?.includes(UserPermission.OnboardingView)) {
      fetchPromises.push(
        asyncDispatchWithErrorToast(
          FetchDashboardsUpdates.request({ type: DashboardsUpdatesType.WaitingForApproval }),
        ),
      )
    }

    await Promise.allSettled(fetchPromises)

    dispatch(SetUserDataLoaded.success(true))
  }, [dispatch, asyncDispatchWithErrorToast])

  const handleFetchUpdates = useCallback(async () => {
    if (
      hasPermission(UserPermission.PaymentsView) &&
      accessInfo?.products?.some((item) => item.product === UserProductName.Payments)
    ) {
      asyncDispatchWithErrorToast(FetchFailedPaymentsUpdates.request())
    }

    if (hasPermission(UserPermission.PartnerDriversView)) {
      asyncDispatchWithErrorToast(
        FetchDashboardsUpdates.request({ type: DashboardsUpdatesType.DisabledContractors }),
      )
    }

    if (hasPermission(UserPermission.OnboardingView)) {
      asyncDispatchWithErrorToast(
        FetchDashboardsUpdates.request({ type: DashboardsUpdatesType.WaitingForApproval }),
      )
    }
  }, [accessInfo, hasPermission])

  usePolling({
    interval: DASHBOARDS_UPDATES_POLLING_INTERVAL,
    shouldStart: () => {
      if (!accessInfo) {
        return false
      }

      const hasPaymentsViewPermission =
        hasPermission(UserPermission.PaymentsView) &&
        accessInfo.products?.some((item) => item.product === UserProductName.Payments)

      return (
        hasPaymentsViewPermission ||
        hasPermission(UserPermission.PartnerDriversView) ||
        hasPermission(UserPermission.OnboardingView)
      )
    },
    callback: handleFetchUpdates,
  })

  return { fetchUserAndMarkets }
}
